//src/components/chat/AgentChat.js
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { apiService } from '../../services/api';
import ChatHeader from './ChatHeader';
import ChatInput from './ChatInput';
import MessageList from './MessageList';
import Loading from '../shared/Loading';
import ConversationItem from './ConversationItem';
import { useSocket } from '../../hooks/useSocket';
import UploadProgressBar from './UploadProgressBar';
import Header from '../shared/Header';
import Card from '../ui/Card';
import Button from '../ui/Button';
import { Search, Filter, X, Phone, Video, MoreVertical, RefreshCw, MessageSquare } from 'lucide-react';
import { useLocation } from 'react-router-dom';

const AgentChat = () => {
    const { user } = useAuth();
    const socket = useSocket();
    const location = useLocation();
    const [conversations, setConversations] = useState([]);
    const [currentChat, setCurrentChat] = useState(null);
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [goToBottom, setGoToBottom] = useState(true);
    const [initialLoading, setInitialLoading] = useState(true);
    const loadingRef = useRef(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadingFile, setUploadingFile] = useState(null);
    // New state variables for conversation list
    const [searchQuery, setSearchQuery] = useState('');
    const [conversationsPage, setConversationsPage] = useState(1);
    const [hasMoreConversations, setHasMoreConversations] = useState(true);
    const [loadingConversations, setLoadingConversations] = useState(false);
    const conversationsEndRef = useRef(null);
    // New state for notification permission
    const [notificationPermission, setNotificationPermission] = useState('default');
    // Ref for tracking window focus state
    const windowFocusedRef = useRef(true);

    // Request notification permission on component mount
    useEffect(() => {
        if ('Notification' in window) {
            setNotificationPermission(Notification.permission);

            // If permission is not determined, we don't auto-request
            // The user will need to click a button to request permission
        }

        // Add event listeners for window focus/blur
        const handleFocus = () => {
            windowFocusedRef.current = true;
        };

        const handleBlur = () => {
            windowFocusedRef.current = false;
        };

        window.addEventListener('focus', handleFocus);
        window.addEventListener('blur', handleBlur);

        return () => {
            window.removeEventListener('focus', handleFocus);
            window.removeEventListener('blur', handleBlur);
        };
    }, []);

    // Function to request notification permission
    const requestNotificationPermission = async () => {
        if ('Notification' in window) {
            try {
                const permission = await Notification.requestPermission();
                setNotificationPermission(permission);
                return permission;
            } catch (error) {
                console.error("Error requesting notification permission:", error);
                return 'denied';
            }
        }
        return 'denied';
    };

    // Function to show notification
    const showNotification = (message) => {
        if (!message || notificationPermission !== 'granted' || windowFocusedRef.current) {
            return;
        }

        // Get sender name
        const senderName = message.from?.name || message.from?.phone || 'New message';

        // Format message content for notification
        let messageContent = 'You received a new message';

        if (message.type === 'text_message' && message.content?.text) {
            messageContent = message.content.text;
        } else if (message.type === 'image_message') {
            messageContent = message.content?.caption || 'Image received';
        } else if (message.type === 'video_message') {
            messageContent = message.content?.caption || 'Video received';
        } else if (message.type === 'audio_message') {
            messageContent = 'Audio message received';
        } else if (message.type === 'document_message') {
            messageContent = 'Document received';
        } else if (message.type === 'location_message') {
            messageContent = 'Location shared';
        } else if (message.type === 'contact_message') {
            messageContent = 'Contact shared';
        } else if (message.type === 'interactive_message') {
            messageContent = message.content?.text || 'Interactive message received';
        }

        // Create notification
        const notification = new Notification(senderName, {
            body: messageContent,
            icon: '/logo192.png', // Adjust path to your app icon
        });

        // Optional: Handle notification click to focus the window
        notification.onclick = () => {
            window.focus();
            if (message.from?.phone) {
                // Find and select this conversation
                const conversation = conversations.find(conv => conv.phone === message.from.phone);
                if (conversation) {
                    handleChatSelect(conversation);
                }
            }
            notification.close();
        };
    };

    // Load initial conversations with search and pagination
    useEffect(() => {
        loadConversations(1, true);
    }, [searchQuery]);

    // Function to load conversations with search and pagination
    const loadConversations = async (page, reset = false) => {
        if (loadingConversations) return;

        try {
            setLoadingConversations(true);
            if (reset) {
                setConversationsPage(1);
                page = 1;
            }

            const response = await apiService.getConversations({
                page: page,
                limit: 50,
                search: searchQuery || undefined
            });

            // Extract conversations and pagination from the response
            const conversations = response.conversations || [];
            const pagination = response.pagination || { hasNextPage: false };

            setHasMoreConversations(pagination.hasNextPage || false);

            if (reset || page === 1) {
                setConversations(conversations);
            } else {
                setConversations(prev => [...prev, ...conversations]);
            }

            setConversationsPage(page);
        } catch (err) {
            setError('Failed to load conversations');
            console.error('Error loading conversations:', err);
        } finally {
            setLoadingConversations(false);
            setLoading(false);
            setTimeout(() => setInitialLoading(false), 100);
        }
    };

    // Handle conversation list scroll for auto pagination
    const handleConversationsScroll = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;

        // When user has scrolled to the bottom (with a small threshold)
        if (scrollHeight - scrollTop - clientHeight < 50) {
            if (hasMoreConversations && !loadingConversations) {
                loadConversations(conversationsPage + 1);
            }
        }
    };

    // Handle search input change
    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
    };

    // Clear search
    const clearSearch = () => {
        setSearchQuery('');
    };

    // Load initial conversations
    useEffect(() => {
        const load = async () => {
            try {
                setLoading(true);
                setInitialLoading(true);
                await loadConversations(1, true);
            } catch (err) {
                setError('Failed to load conversations');
                console.error('Error loading conversations:', err);
            } finally {
                setLoading(false);
                // Set initialLoading to false after a short delay to prevent immediate loadMore trigger
                setTimeout(() => setInitialLoading(false), 100);
            }
        };
        load();
    }, []);

    // Separate effect to handle URL query parameters
    useEffect(() => {
        // Only proceed if we have conversations loaded
        if (!loadingConversations && conversations.length > 0) {
            const queryParams = new URLSearchParams(location.search);
            const phoneParam = queryParams.get('phone');

            if (phoneParam) {
                console.log('Phone param detected:', phoneParam);
                const targetConversation = conversations.find(conv => conv.phone === phoneParam);
                if (targetConversation) {
                    console.log('Found matching conversation:', targetConversation);
                    handleChatSelect(targetConversation);
                } else {
                    console.log('No matching conversation found for phone:', phoneParam);
                }
            }
        }
    }, [location.search, conversations, loadingConversations]);

    useEffect(() => {
        console.log('Socket------>>>:', socket);
        if (!socket.connected) return;

        const handleNewMessage = (data) => {
            if (!data.message) return;

            setGoToBottom(true);
            if (currentChat?.phone === data.message.from?.phone) {
                setMessages(prevMessages => [...prevMessages, data.message]);

                // Mark message as read
                if (data.message.direction === 'incoming' && data.message.status !== 'read') {
                    apiService.markMessagesAsRead([data.message]);
                }
            }

            // Show notification for incoming messages when window is not focused
            if (data.message.direction === 'incoming') {
                showNotification(data.message);
            }

            // Update conversations list, handling both existing and new conversations
            setConversations(prevConversations => {
                const existingChatIndex = prevConversations.findIndex(
                    chat => chat.phone === data.message.from?.phone
                );

                // Get appropriate message content display text based on message type
                const getMessageDisplayText = (message) => {
                    if (!message) return "New message";

                    switch (message.type) {
                        case 'text_message':
                            return message.content?.text || "Text message";
                        case 'image_message':
                            return message.content?.caption || "Image";
                        case 'video_message':
                            return message.content?.caption || "Video";
                        case 'audio_message':
                            return "Audio message";
                        case 'document_message':
                            return "Document";
                        case 'template_message':
                            return `Template: ${message.content?.template?.name || ""}`;
                        default:
                            return "New message";
                    }
                };

                // Extract session information from data
                const sessionInfo = data.session || null;

                if (existingChatIndex !== -1) {
                    // Update existing conversation
                    const updatedConversations = [...prevConversations];
                    const chat = updatedConversations[existingChatIndex];

                    updatedConversations[existingChatIndex] = {
                        ...chat,
                        lastMessage: data.message,
                        unreadCount: chat.phone !== currentChat?.phone
                            ? (chat.unreadCount || 0) + 1
                            : chat.unreadCount,
                        // Add session information if it exists
                        session: sessionInfo || chat.session
                    };

                    // Move updated conversation to top
                    const [updatedChat] = updatedConversations.splice(existingChatIndex, 1);
                    return [updatedChat, ...updatedConversations];
                } else {
                    // Add new conversation at the top
                    const newConversation = {
                        phone: data.message.from?.phone,
                        name: data.message.from?.name || data.message.from?.phone,
                        lastMessage: data.message,
                        unreadCount: 1,
                        // Add session information if it exists
                        session: sessionInfo
                    };
                    return [newConversation, ...prevConversations];
                }
            });
        };

        const handleAgentMessage = (data) => {
            if (data.direction === "outgoing" && currentChat?.phone === data.to?.phone) {
                setGoToBottom(true);
                setMessages(prevMessages => [...prevMessages, data]);
            }
        };

        const handleStatusUpdate = (data) => {
            if (!data.messageId || !data.status) return;

            setMessages(prevMessages =>
                prevMessages.map(msg =>
                    msg.messageId === data.messageId
                        ? { ...msg, status: data.status }
                        : msg
                )
            );
        };

        console.log('Adding socket listeners');
        socket.onMessage(handleNewMessage);
        socket.onAgentMessageSent(handleAgentMessage);
        socket.onStatusUpdate(handleStatusUpdate);


        // Cleanup
        return () => {
            socket.off('whatsapp_message_received', handleNewMessage);
            socket.off('agent_message_sent', handleAgentMessage);
            socket.off('message_status_update', handleStatusUpdate);
        };
    }, [socket.connected, currentChat]);

    const handleChatSelect = async (chat) => {
        try {
            // Reset states
            setCurrentChat(chat);
            setMessages([]);
            setCurrentPage(1);
            setHasMore(true);
            setGoToBottom(true);
            setInitialLoading(true);

            const data = await apiService.getChatHistory(chat.phone, 1, 50);
            console.log('Chat History:', data);
            setMessages(data.messages);
            setHasMore(data.pagination.hasMore);

            // Mark messages as read
            const unreadMessages = data.messages.filter(
                msg => msg.direction === 'incoming' && msg.status !== 'read'
            );
            if (unreadMessages.length > 0) {
                await apiService.markMessagesAsRead(unreadMessages);
                // Update conversation unread count
                setConversations(prevConversations =>
                    prevConversations.map(conv =>
                        conv.phone === chat.phone
                            ? { ...conv, unreadCount: 0 }
                            : conv
                    )
                );
            }
        } catch (err) {
            setError('Failed to load chat history');
            console.error('Error loading chat history:', err);
        } finally {
            setTimeout(() => setInitialLoading(false), 100);
        }
    };

    const loadMoreMessages = async () => {
        if (!currentChat || loadingRef.current || !hasMore || initialLoading) return;

        try {
            loadingRef.current = true;
            setLoadingMore(true);
            setGoToBottom(false);

            const nextPage = currentPage + 1;
            const data = await apiService.getChatHistory(currentChat.phone, nextPage, 50);

            // Add new messages to the beginning of the list
            setMessages(prevMessages => {
                const newMessages = [...data.messages, ...prevMessages];
                return newMessages.filter((message, index, self) =>
                    index === self.findIndex(m => m.messageId === message.messageId)
                );
            });

            setCurrentPage(nextPage);
            setHasMore(data.pagination.hasMore);

        } catch (err) {
            setError('Failed to load more messages');
            console.error('Error loading more messages:', err);
        } finally {
            // Add a small delay before allowing next load
            setTimeout(() => {
                loadingRef.current = false;
                setLoadingMore(false);
            }, 1000);
        }
    };

    const handleSendMessage = async (message) => {
        if (!currentChat || !message.trim()) return;

        try {
            await apiService.sendMessage(currentChat.phone, message, currentChat.name);
            setGoToBottom(true);
        } catch (err) {
            setError('Failed to send message');
            console.error('Error sending message:', err);
        }
    };

    const handleSendMedia = async (file, caption) => {
        if (!currentChat || !file) return;

        // Add file size validation
        const MAX_FILE_SIZE = 16 * 1024 * 1024; // 16MB
        if (file.size > MAX_FILE_SIZE) {
            setError('File size exceeds 16MB limit');
            return;
        }

        try {
            setUploadingFile(file);
            setUploadProgress(0);

            const xhr = new XMLHttpRequest();

            await new Promise((resolve, reject) => {
                // Add timeout handler
                xhr.timeout = 30000; // 30 seconds timeout
                xhr.ontimeout = () => reject(new Error('Upload timed out'));

                xhr.upload.addEventListener('progress', (event) => {
                    if (event.lengthComputable) {
                        const progress = (event.loaded / event.total) * 100;
                        setUploadProgress(Math.min(99, progress)); // Cap at 99% until fully complete
                    }
                });

                xhr.addEventListener('load', () => {
                    if (xhr.status >= 200 && xhr.status < 300) {
                        setUploadProgress(100); // Set to 100% on success
                        resolve(xhr.response);
                    } else {
                        reject(new Error(`Upload failed: ${xhr.statusText}`));
                    }
                });

                xhr.addEventListener('error', () => {
                    reject(new Error('Network error occurred during upload'));
                });

                xhr.addEventListener('abort', () => {
                    reject(new Error('Upload was cancelled'));
                });

                const formData = new FormData();
                formData.append('file', file);
                formData.append('recipientPhone', currentChat.phone);
                formData.append('recipientName', currentChat.name);
                formData.append('type', apiService.getFileType(file));
                if (caption) formData.append('caption', caption);

                const token = localStorage.getItem('token');
                if (!token) {
                    reject(new Error('Authentication token not found'));
                    return;
                }
                const API_BASE_URL = window._env_ ? window._env_.API_URL : process.env.REACT_APP_API_URL;
                xhr.open('POST', `${API_BASE_URL}/api/agent/media`);
                xhr.setRequestHeader('Authorization', `Bearer ${token}`);
                xhr.responseType = 'json';
                xhr.send(formData);
            });

            setGoToBottom(true);
        } catch (err) {
            setError(err.message || 'Failed to send media');
            console.error('Error sending media:', err);
        } finally {
            // Delay cleanup to show completed state
            setTimeout(() => {
                setUploadingFile(null);
                setUploadProgress(0);
            }, 1500); // Increased delay to show completion
        }
    };

    return (
        /* this div height should be caluclated screen height - 100px   */
        <div className="flex h-screen bg-gray-100" style={{ height: `calc(100vh - 75px)` }}>
            {/* Conversations List */}
            <div className="w-80 bg-white border-r flex flex-col">
                <div className="p-4 border-b flex flex-col gap-2">
                    <h1 className="text-xl font-semibold text-gray-800">Conversations</h1>
                    <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Search className="h-4 w-4 text-gray-400" />
                        </div>
                        <input
                            type="text"
                            className="w-full pl-10 pr-10 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Search by phone..."
                            value={searchQuery}
                            onChange={handleSearchChange}
                        />
                        {searchQuery && (
                            <button
                                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                onClick={clearSearch}
                            >
                                <X className="h-4 w-4 text-gray-400" />
                            </button>
                        )}
                    </div>

                    {/* Notification Permission Request */}
                    {notificationPermission !== 'granted' && (
                        <button
                            onClick={requestNotificationPermission}
                            className="mt-2 w-full py-2 text-sm font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                        >
                            Enable Notifications
                        </button>
                    )}
                </div>
                <div
                    className="flex-1 overflow-y-auto"
                    onScroll={handleConversationsScroll}
                    ref={conversationsEndRef}
                >
                    {conversations.map(chat => (
                        <ConversationItem
                            key={chat.phone}
                            conversation={chat}
                            isActive={currentChat?.phone === chat.phone}
                            onClick={() => handleChatSelect(chat)}
                        />
                    ))}
                    {loadingConversations && (
                        <div className="py-4 text-center text-gray-500">
                            <RefreshCw className="h-5 w-5 mx-auto animate-spin" />
                            <span className="text-sm">Loading more...</span>
                        </div>
                    )}
                    {!hasMoreConversations && conversations.length > 0 && (
                        <div className="py-4 text-center text-gray-500 text-sm">
                            No more conversations
                        </div>
                    )}
                    {!loadingConversations && conversations.length === 0 && (
                        <div className="py-8 text-center text-gray-500">
                            <MessageSquare className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                            <p>No conversations found</p>
                            {searchQuery && (
                                <p className="text-sm mt-1">Try a different search term</p>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Chat Area */}
            <div className="flex-1 flex flex-col">
                {currentChat ? (
                    <>
                        <ChatHeader currentChat={currentChat} />
                        <MessageList
                            messages={messages}
                            loading={loadingMore}
                            hasMore={hasMore}
                            onLoadMore={loadMoreMessages}
                            goToBottom={goToBottom}
                            initialLoading={initialLoading}
                        />
                        <ChatInput
                            onSendMessage={handleSendMessage}
                            onSendMedia={handleSendMedia}
                            disabled={!currentChat}
                        />
                    </>
                ) : (
                    <div className="flex-1 flex items-center justify-center bg-gray-50">
                        <div className="text-center text-gray-500">
                            Select a conversation to start messaging
                        </div>
                    </div>
                )}
            </div>
            {/* Upload Progress Bar */}
            {uploadingFile && (
                <UploadProgressBar
                    progress={uploadProgress}
                    fileName={uploadingFile.name}
                />
            )}
            {/* Error Toast */}
            {error && (
                <div className="fixed top-4 right-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded shadow-lg">
                    {error}
                    <button
                        onClick={() => setError(null)}
                        className="ml-4 text-red-700 hover:text-red-900"
                    >
                        ×
                    </button>
                </div>
            )}
        </div>
    );
};

export default AgentChat;